import { Button, Modal } from '@vadiun/react-components';
import { FormControl, InputLabel, MenuItem } from '@material-ui/core';
import { TextField, Select } from 'formik-material-ui';
import { Formik, Field, Form } from 'formik';
import React, { FC } from 'react';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { ViajeTrasladoTipoServicio } from 'app/viaje/models';
import { TransferReservationType } from 'app/viaje/viajeTraslado/viajeTraslado/models/TransferReservationPartial';
import TravelsIdsForClient from './TravelsIdsForClient';
import { STATUS_COLORS } from 'app/viaje/viajeTraslado/viajeTraslado/constants/paymentStatusColors';

interface LoadBillingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleBill: (number: string, letter: string) => void;
  selectedGroups: {
    clientId: number;
    clientName: string;
    companyName?: string;
    service: ViajeTrasladoTipoServicio;
    type: TransferReservationType;
    ids: number[];
  }[];
  selectedTravels: {
    id: number;
    amount: number;
  }[];
}

interface ManualBillingsFormData {
  number: string;
  letter: string;
}

const letterOptions = [
  { id: 'A', name: 'A' },
  { id: 'B', name: 'B' },
  { id: 'C', name: 'C' }
];

const LoadBillingsModal: FC<LoadBillingsModalProps> = ({
  isOpen,
  onClose,
  handleBill,
  selectedGroups,
  selectedTravels
}) => {
  // Calcular el monto total
  const totalAmount = selectedTravels.reduce(
    (sum, travel) => sum + travel.amount,
    0
  );
  const validationSchema = Yup.object().shape({
    number: Yup.string()
      .min(3, 'mínimo 3 caracteres')
      .required('El número es requerido'),
    letter: Yup.string().required('La letra es requerida')
  });
  const initialValues: ManualBillingsFormData = {
    number: '',
    letter: 'B'
  };
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="sm"
      body={
        <div className="relative">
          <div className="text-center mb-6 mt-10">
            <div className="flex items-center justify-center gap-3 mb-2 relative">
              <div
                className="rounded-full flex items-center justify-center"
                style={{
                  boxShadow: '-6px 3px 15px rgba(245, 73, 39, 0.4)',
                  position: 'absolute',
                  top: '-62px',
                  right: '260px',
                  width: '70px',
                  height: '70px'
                }}
              >
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="text-white text-5xl"
                  style={{
                    color: '#F54927'
                  }}
                />
              </div>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  boxShadow: '6px -3px 10px rgba(255, 153, 0, 0.4)',
                  position: 'absolute',
                  top: '-70px',
                  right: '220px'
                }}
              >
                <FontAwesomeIcon
                  icon={faArrowUp}
                  className="text-white text-2xl"
                  style={{
                    color: '#FF9900'
                  }}
                />
              </div>
            </div>
            <h2
              className="text-2xl font-bold  mt-6"
              style={{
                color: '#B01E00',
                textShadow: '1px 1px 2px rgba(245, 73, 39, 0.4)'
              }}
            >
              Cargar Factura
            </h2>
            <p className="text-gray-600 mt-1">
              Ingrese los datos de la factura para los viajes seleccionados
            </p>
            <div className="flex w-full mt-4 p-3 bg-gray-50  justify-center items-center gap-4">
              <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
                Monto Total:
              </span>
              <span
                className="text-lg font-bold"
                style={{ color: STATUS_COLORS.APPROVED }}
              >
                ${totalAmount}
              </span>
            </div>
            {selectedGroups && selectedGroups.length > 0 ? (
              <div className="flex flex-col mt-3 text-left">
                <span className="text-sm text-gray-600">Seleccionados:</span>
                <div className="flex flex-wrap gap-2 mt-2 w-full">
                  {selectedGroups.map((group) => (
                    <div key={group.clientId} className="group relative w-auto">
                      <TravelsIdsForClient
                        client={{
                          name: group.clientName,
                          companyName: group.companyName,
                          type: group.type,
                          service: group.service
                        }}
                        groupIds={group.ids}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <Formik<ManualBillingsFormData>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              handleBill(values.number, values.letter);
              onClose();
            }}
          >
            {({ isSubmitting, errors, touched, submitCount, dirty }) => (
              <Form className="flex flex-col gap-4">
                <Field
                  name="number"
                  label="Número de Factura"
                  variant="outlined"
                  component={TextField}
                  type="number"
                  fullWidth
                />
                <FormControl
                  variant="outlined"
                  fullWidth
                  error={!!errors.letter && (touched.letter || submitCount > 0)}
                >
                  <InputLabel>Tipo de Factura</InputLabel>
                  <Field
                    component={Select}
                    name="letter"
                    label="Tipo de Factura"
                  >
                    {letterOptions.map((letter) => (
                      <MenuItem key={letter.id} value={letter.id}>
                        {letter.name}
                      </MenuItem>
                    ))}
                  </Field>
                  {errors.letter && (touched.letter || submitCount > 0) && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.letter}
                    </div>
                  )}
                </FormControl>

                <div
                  className="flex justify-between gap-2 pt-4"
                  style={{
                    marginBottom: '-16px'
                  }}
                >
                  <Button
                    type="button"
                    variant="light"
                    color="gray"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !dirty}
                  >
                    Facturar y Enviar
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      }
    />
  );
};
export default LoadBillingsModal;
